import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';
import { stAnimation, STAnimationType } from '../../utils/ScrollTriggerHelper';
import gsap from 'gsap';

const DEBUG_VERBOSE: boolean = false;
export const HHDSImageTagName: string = 'hhds-image';
const TAG_NAME: string = HHDSImageTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

const DEFER_ASPECT_CHANGE_UNTIL_IMAGE_LOAD: boolean = true;

export enum HHDSImageEvent {
	load = 'hhds-image-load',
}

export const HHDSImageAttrNames = {
	src: 'src',
	//aspect: 'aspect',
	alt: 'alt',
	animate: 'animate',
	srcSet: 'srcset',
	sizes: 'sizes',
	parallaxStrength: 'parallax-strength',
	parallaxReverse: 'parallax-reverse',
	lazy: 'lazy',
};

const Attrs = HHDSImageAttrNames;

export class HHDSImage extends Component {
	private imageLoadFunc: any;
	private pendingAspect: string | null = null;
	private parallaxTween: gsap.core.Tween | null = null;

	constructor() {
		super();
		this.imageLoadFunc = () => {
			DEFER_ASPECT_CHANGE_UNTIL_IMAGE_LOAD && this.applyPendingAspect();
			this.emitEvent(HHDSImageEvent.load);
		};
	}

	private applyPendingAspect(): void {
		if (this.pendingAspect) {
			if (this.pendingAspect === '-') {
				this.style.removeProperty('--image-aspect-ratio');
			} else {
				this.style.setProperty('--image-aspect-ratio', this.pendingAspect);
			}
			this.pendingAspect = null;
		}
	}

	get imageRatio(): number {
		return this.image.naturalWidth / this.image.naturalHeight;
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		const alt = this.vars.get<string>(Attrs.alt);
		const animate = this.vars.get<boolean>(Attrs.animate);
		const lazyAttr: string = this.vars.get<boolean>(Attrs.lazy) ? 'loading="lazy"' : '';
		const html: string = /* html */ `<figure class="${TAG_NAME}">
				<figcaption class="${TAG_NAME}__caption"><slot name="caption_above"></slot></figcaption>
				<div class="${TAG_NAME}__container ${animate ? '${TAG_NAME}__container--animate' : ''}">
					<img  alt="${alt}" ${lazyAttr} class="${TAG_NAME}__img" ${this.srcAttributes}/>
				</div>
				<figcaption class="${TAG_NAME}__caption"><slot name="caption"></slot></figcaption>
			</figure>
		`;

		this.setInnerHtml('', '', html);
		this.observeSlotChanges(true);

		this.image.addEventListener('load', this.imageLoadFunc);
		const parallaxStrength: number = this.vars.get<number>(Attrs.parallaxStrength);
		if (parallaxStrength > 0) {
			const reverse = this.vars.get<boolean>(Attrs.parallaxReverse);
			this.configureParallax(parallaxStrength, reverse);
		}
	}

	get srcAttributes() {
		const hasSrcSet = this.vars.get<string>(Attrs.srcSet) !== '';
		if (!hasSrcSet) {
			return `src="${this.vars.get<string>(Attrs.src)}"`;
		}
		const attrSrcSet = this.vars.get<string>(Attrs.srcSet);
		const attrSizes = this.vars.get<string>(Attrs.sizes);

		DEBUG_VERBOSE &&
			logger.log(`${LOG_PREFIX} markupResponsiveImages`, {
				attrSrcSet,
				attrSizes,
			});

		if (attrSrcSet !== '' && attrSizes !== '') {
			return `srcset="${attrSrcSet}" sizes="${attrSizes}"`;
		} else {
			return '';
		}
	}

	configureParallax(strength: number, reverse: boolean): void {
		const component = this.element(`.${TAG_NAME}`);
		const img = this.image;
		img.style.transitionDuration = '0s'; // Remove any transition duration that might conflict with the parallax effect
		img.style.transform = `scale(${strength})`;
		this.parallaxTween = stAnimation(component, STAnimationType.bidirectional, {
			start: '0% bottom',
			end: '100% top',
			manualUpdate: (progress: number) => {
				let remappedProgress = (progress - 0.5) * (reverse ? -1 : 1);
				const yPercent = remappedProgress * (strength - 1.0) * 100;
				gsap.set(img, { yPercent: yPercent });
			},
		});
	}

	get image(): HTMLImageElement {
		return this.root.querySelector('img') as HTMLImageElement;
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
		this.image.removeEventListener('load', this.imageLoadFunc);
		this.parallaxTween?.scrollTrigger?.kill();
		this.parallaxTween?.kill();
		this.parallaxTween = null;
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: Element[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.src]: {
		description: 'The source URL of the image. Supports formats the main img formats like jpg, png and webp and also svg.',
		defaultValue: '',
		type: String,
	},
	[Attrs.srcSet]: {
		description:
			'Defines the set of images we will allow the browser to choose between, and what size each image is. Supports formats the main img formats like jpg, png and webp and also svg.',
		defaultValue: '',
		type: String,
	},
	[Attrs.sizes]: {
		description:
			'Defines a set of media conditions (e.g. screen widths) and indicates what image size would be best to choose, when certain media conditions are true.',
		defaultValue: '',
		type: String,
	},
	/*[Attrs.aspect]: {
		description: 'Optional aspect ratio for the image.',
		defaultValue: '',
		type: String,
	},*/
	[Attrs.alt]: {
		description: 'The alt text for the image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.animate]: {
		description: 'Defines an animation',
		defaultValue: 'false',
		type: Boolean,
	},
	[Attrs.parallaxStrength]: {
		description: 'Defines the strength of the parallax effect',
		defaultValue: 0,
		type: Number,
	},
	[Attrs.parallaxReverse]: {
		description: 'Defines the direction of the parallax effect',
		defaultValue: false,
		type: Boolean,
	},
	[Attrs.lazy]: {
		description: 'Defines if the image should be lazy loaded',
		defaultValue: true,
		type: Boolean,
	},
};
